import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { IconFileUpload, IconFiles } from "@tabler/icons-react";
import useToast from "hooks/useToast";
import FilesTable from "./FilesTable";
import { FileProps } from ".";
import SingleFileView from "./SingleFileView";

interface FileUploaderLocalProps extends React.HTMLProps<HTMLInputElement> {
  containerClassName?: string;
  labelName?: string;
  files?: File[];
  onRemove?: (id: string) => void;
  handleChange?: (images: File[]) => void | Promise<void>;
  multiple?: boolean;
  onDraged?: (filesList: File[]) => void;
  onRemoveSelected?: (file: File) => void;
  buttonText?: string;
  onlyImage?: boolean;
  setInProgress?: (value: boolean) => void;
  maxFileSize?: number; // in bytes
  uploadedFiles?: FileProps[];
  removeUploadedFiles?: (id: string) => void;
  fileTypes?: string[];
  fileIcon?: React.ReactElement;
}

type SelectedFiles = File[];

const FileUploaderLocal = (props: FileUploaderLocalProps) => {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = React.useState<SelectedFiles>(
    props.files ?? []
  );
  const [isDragging, setIsDragging] = React.useState(false);
  const toast = useToast();

  async function removeFile(file: File) {
    const files = selectedFiles.filter((item) => {
      if (file instanceof File && item instanceof File) {
        return item !== file;
      }
      return item;
    });
    if (props.handleChange && props.files) {
      await props.handleChange?.(files);
    } else {
      setSelectedFiles(files);
      await props.handleChange?.(files);
      props.onRemoveSelected?.(file);
    }
  }

  function checkAllowedFileSize(imageFiles: File[]) {
    const validFiles = imageFiles.filter(
      (file) => !props.maxFileSize || file.size <= props.maxFileSize
    );

    if (props.maxFileSize && validFiles.length != imageFiles.length) {
      toast(
        t("commons.maxAllowedFileSize", {
          size: props.maxFileSize / 1024 / 1024,
        }),
        "attention"
      );
    }
    return validFiles;
  }

  function checkAllowedFileTypes(files: File[]) {
    if (!props.fileTypes || props.fileTypes.length === 0) {
      return files;
    }

    const allowedTypes = props.fileTypes.map((type) => type.toLowerCase());

    const validFiles = files.filter((file) => {
      const fileType = file.type.toLowerCase(); // mime type
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;

      return (
        allowedTypes.includes(fileType) || allowedTypes.includes(fileExtension)
      );
    });

    if (validFiles.length !== files.length) {
      toast(t("commons.invalidFileType"), "attention");
    }

    return validFiles;
  }

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) {
      const filesList = Array.from(e.dataTransfer.files);

      const typeFilteredFiles = checkAllowedFileTypes(filesList);

      const validFiles = checkAllowedFileSize(typeFilteredFiles);

      if (validFiles.length === 0) {
        setIsDragging(false);
        return;
      }
      props.onDraged?.(validFiles as File[]);
      handleChange(e.dataTransfer.files);
      setIsDragging(false);
    }
  };

  const handleFileAdded = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesList = Array.from(e.target.files);

      const typeFilteredFiles = checkAllowedFileTypes(filesList);

      const validFiles = checkAllowedFileSize(typeFilteredFiles);
      props.onChange?.(e);
      if (e.target.files) {
        handleChange(validFiles);
      }
    }
  };

  const onDraggEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDraggLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleChange = async (filesList: File[] | FileList) => {
    const nextState = Array.from(
      new Set([
        ...(props.multiple ? selectedFiles : []),
        ...Array.from(filesList),
      ])
    );
    if (props.handleChange && props.files) {
      await props.handleChange?.(nextState);
    } else {
      setSelectedFiles(nextState);
      await props.handleChange?.(nextState);
    }
  };

  React.useEffect(() => {
    if (props.files) {
      setSelectedFiles(props.files);
    }
  }, [props.files]);

  function emptyState() {
    return (
      <>
        {props.fileIcon ? (
          React.cloneElement(props.fileIcon, {
            className: clsx(
              "mb-4 min-w-16 min-h-16",
              isDragging
                ? "text-indigo-500 pointer-events-none"
                : "text-gray-500"
            ),
            strokeWidth: 1,
            width: 64,
          })
        ) : (
          <IconFiles
            width={64}
            className={clsx(
              "mb-4 min-w-16 min-h-16",
              isDragging
                ? "text-indigo-500 pointer-events-none"
                : "text-gray-500"
            )}
            strokeWidth={1}
          />
        )}
        {isDragging ? (
          <span className="text-sm font-medium text-indigo-700 pointer-events-none">
            {t("commons.dropHere")}
          </span>
        ) : (
          <>
            <span className="mb-2 text-sm font-medium">
              {t("commons.dragAndDropMessage")}
            </span>
            <span className="mb-3 text-sm">{t("commons.or")}</span>
            <div
              className={clsx(
                "bg-indigo-100 text-indigo-700 hover:bg-indigo-200 h-10",
                "flex px-4 py-3 text-base justify-center items-center font-medium rounded-md"
              )}
            >
              {(props.buttonText || props.multiple) && props.multiple
                ? t("commons.selectFiles")
                : t("commons.selectFile")}
              <IconFileUpload className="ml-2 min-h-5 min-w-5" stroke={2} />
            </div>
          </>
        )}
      </>
    );
  }

  const hasAnyFiles = selectedFiles.length || props.uploadedFiles?.length;
  const showSelectedFile = selectedFiles.length || props.uploadedFiles?.length;

  const openFileSelector = () => {
    inputRef?.current?.click();
  };

  function selectedFileView() {
    if (props.multiple)
      return (
        <FilesTable
          files={selectedFiles}
          uploadedFiles={props.uploadedFiles}
          onRemoveFile={removeFile}
          onRemoveUploadedFile={props.removeUploadedFiles}
        />
      );
    else {
      return (
        <SingleFileView
          files={selectedFiles}
          uploadedFiles={props.uploadedFiles}
          onRemoveFile={removeFile}
          onRemoveUploadedFile={props.removeUploadedFiles}
          openFileSelector={openFileSelector}
        />
      );
    }
  }

  return (
    <div
      className={clsx("flex flex-col mt-1 mb-2", props.containerClassName, {
        "-mt-9": hasAnyFiles,
      })}
    >
      {props.labelName && (
        <label
          className={clsx("text-sm font-medium text-gray-700", {
            "-mb-7 pt-4": hasAnyFiles,
          })}
        >
          {props.labelName}
        </label>
      )}
      <div
        className={clsx(
          "flex flex-wrap gap-3 items-start",
          hasAnyFiles ? "mb-3 justify-end" : ""
        )}
      >
        <label
          className={clsx(
            "flex flex-col items-center justify-center transition border cursor-pointer",
            "appearance-none text-gray-700 hover:bg-gray-50 transition",
            "focus:outline-none rounded-md",
            hasAnyFiles
              ? "!h-6 !border-gray-300"
              : "h-56 w-full border-dashed p-6",
            isDragging
              ? "bg-indigo-50 border-indigo-600"
              : "bg-white border-gray-300 hover:border-gray-400",
            { invisible: hasAnyFiles && !props.multiple }
          )}
          onDrop={(e) => handleDrop(e).catch((err) => console.error(err))}
          onDragOver={onDraggEnter}
          onDragEnter={onDraggEnter}
          onDragLeave={onDraggLeave}
        >
          <input
            ref={inputRef}
            type="file"
            onBlur={props.onBlur}
            name={props.name}
            id={props.id}
            required={props.required}
            className="hidden"
            accept={props.fileTypes?.join(",") ?? ".pdf"}
            multiple={props.multiple}
            onChange={handleFileAdded}
          />

          {hasAnyFiles && props.multiple ? (
            <div
              className={clsx(
                "px-2 flex text-xs !h-6 justify-center items-center font-semibold rounded-md",
                "hover:bg-gray-100 text-gray-700"
              )}
            >
              {t("commons.uploadDocument")}
            </div>
          ) : null}

          {!hasAnyFiles ? emptyState() : null}
        </label>
      </div>
      {showSelectedFile ? selectedFileView() : null}
    </div>
  );
};

FileUploaderLocal.displayName = "FileUploaderLocal";

export default FileUploaderLocal;
